import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../api';

export default function QRSheetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [labelMode, setLabelMode] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
    } catch { setError('Job not found'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Inject Dymo @page size only when label mode is active
  useEffect(() => {
    const styleId = 'dymo-page-style';
    let el = document.getElementById(styleId);
    if (labelMode) {
      if (!el) { el = document.createElement('style'); el.id = styleId; document.head.appendChild(el); }
      el.textContent = '@media print { @page { size: 2.125in 1in; margin: 0; } }';
    } else {
      if (el) el.remove();
    }
    return () => { const s = document.getElementById(styleId); if (s) s.remove(); };
  }, [labelMode]);

  if (loading) return <div className="loading-screen">Loading…</div>;
  if (!job) return <div className="loading-screen">{error}</div>;

  const origin = window.location.origin;
  const allOps = (job.assemblies || []).flatMap(asm =>
    (asm.operations || []).map(op => ({ ...op, assembly_name: asm.name, part_number: asm.part_number }))
  );

  return (
    <div className={labelMode ? 'label-mode' : ''}>

      {/* Screen-only controls */}
      <div className="qr-screen-controls">
        <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate(`/jobs/${id}`)}>
          ← Back to job
        </button>
        <div style={{ fontWeight:700, fontSize:17 }}>{job.job_number} — {job.title}</div>
        <div style={{ display:'flex', gap:8 }}>
          <button
            className={`btn ${labelMode ? 'btn-primary' : 'btn-ghost'}`}
            style={{ padding:'6px 14px', fontSize:13 }}
            onClick={() => setLabelMode(m => !m)}
          >
            {labelMode ? 'Label mode ✓' : 'Dymo label mode'}
          </button>
          <button className="btn btn-primary" style={{ padding:'6px 14px', fontSize:13 }} onClick={() => window.print()}>
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* ── LABEL MODE ── */}
      {labelMode && (
        <div className="label-print-wrapper">
          {/* Screen preview note */}
          <div className="label-preview-note">
            Previewing {allOps.length} labels at 2.125" × 1" — click <strong>Print / Save PDF</strong> above.
            In your print dialog set paper size to <strong>2.125" × 1"</strong> and margins to <strong>None</strong>.
          </div>

          <div className="label-list">
            {allOps.map(op => (
              <div key={op.id} className="dymo-label">
                <QRCodeSVG
                  value={`${origin}/work?op=${op.id}`}
                  size={72}
                  level="M"
                  className="label-qr"
                />
                <div className="label-text">
                  <div className="label-job">{job.job_number}</div>
                  <div className="label-asm">{op.assembly_name}{op.part_number ? ` · ${op.part_number}` : ''}</div>
                  <div className="label-op">{op.op_code && <span style={{ fontFamily:'monospace', color:'#2563eb', marginRight:4 }}>{op.op_code}</span>}{op.name}</div>
                  {op.estimated_minutes && (
                    <div className="label-est">Est. {op.estimated_minutes}m</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SHEET MODE ── */}
      {!labelMode && (
        <div className="qr-print-page">
          <div className="qr-print-header">
            <div style={{ fontSize:20, fontWeight:800 }}>{job.job_number}</div>
            <div style={{ fontSize:15, fontWeight:600, color:'#444', marginTop:2 }}>{job.title}</div>
            {job.due_date && (
              <div style={{ fontSize:12, color:'#666', marginTop:2 }}>
                Due: {new Date(job.due_date).toLocaleDateString()}
              </div>
            )}
            <div style={{ fontSize:11, color:'#999', marginTop:2 }}>
              Printed {new Date().toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' })}
              {' · '}{allOps.length} operations
            </div>
          </div>

          {(job.assemblies || []).map(asm => (
            asm.operations?.length > 0 && (
              <div key={asm.id} className="qr-assembly-section">
                <div className="qr-assembly-title">
                  {asm.name}
                  {asm.part_number && <span style={{ fontWeight:400, color:'#666', marginLeft:8 }}>P/N: {asm.part_number}</span>}
                  {asm.quantity > 1 && <span style={{ fontWeight:400, color:'#666', marginLeft:8 }}>×{asm.quantity}</span>}
                </div>
                <div className="qr-ops-grid">
                  {asm.operations.map(op => (
                    <div key={op.id} className="qr-op-card">
                      <QRCodeSVG
                        value={`${origin}/work?op=${op.id}`}
                        size={110}
                        level="M"
                        style={{ flexShrink:0 }}
                      />
                      <div className="qr-op-info">
                        <div className="qr-op-name">
                          {op.op_code && <span style={{ fontFamily:'monospace', color:'#2563eb', marginRight:5 }}>{op.op_code}</span>}
                          {op.name}
                        </div>
                        {op.description && <div className="qr-op-desc">{op.description}</div>}
                        {op.estimated_minutes && (
                          <div className="qr-op-est">Est. {op.estimated_minutes} min</div>
                        )}
                        <div className="qr-op-job">{job.job_number} · {asm.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
